import { createSlice } from '@reduxjs/toolkit'

const INITIAL_STATE = {
  loading: false,
  homeLoading: false,
  gameCategory: {},
  gameSubCategory: {},
  games: {},
  providers: [],
  providersByOrder: [],
  studios: [],
  studiosByOrder: [],
  gameSearch: {},
  favoriteGamesList: {
    fetched: false
  },
  filteredGames: null,
  filteredLoading: true,
  fetchMoreLoading: false,
  favLoading: false,
  subCatLoading: false,
  rowsFetchedCount: 0,
  categoryProviderList: null,
  categoryStudioList: null,
  providerListLoading: false,
  studioListLoading: false,
  recentGames: null,
  recentGamesLoading: false,
  // Winners datasets
  topWinners: [],
  highWinners: [],
  luckyWinners: [],
  currentWinners: [],
  topPlayers: [],
  topPlayersLoading: false,
  topWinnersLoading: false,
  highWinnersLoading: false,
  luckyWinnersLoading: false
}

const {
  actions: {
    getGameCategoryStart,
    getGameCategorySuccess,
    getGameCategoryFailure,
    getGameSubCategoryStart,
    getGameSubCategorySuccess,
    getGameSubCategoryFailure,
    getGameSearch,
    getFavGameStart,
    getFavGameSuccess,
    getFavGameFailure,
    getHomeDataStart,
    getHomeDataSuccess,
    getHomeDataFailure,
    getProvidersStart,
    getProvidersSuccess,
    getProvidersFailure,
    getProvidersByOrderStart,
    getProvidersByOrderSuccess,
    getProvidersByOrderFailure,
    getStudiosStart,
    getStudiosSuccess,
    getStudiosFailure,
    setStudiosByOrder,
    toggleFavoriteStart,
    toggleFavoriteSuccess,
    toggleFavoriteFailure,
    getFilteredGamesStart,
    getFilteredGamesSuccess,
    getFilteredGamesFailure,
    getTopGamesStart,
    getTopGamesSuccess,
    getTopGamesFailure,
    getTopWinnersStart,
    getTopWinnersSuccess,
    getTopWinnersFailure,
    getTopPlayersStart,
    getTopPlayersSuccess,
    getTopPlayersFailure,
    getHighWinnersStart,
    getHighWinnersSuccess,
    getHighWinnersFailure,
    getLuckyWinnersStart,
    getLuckyWinnersSuccess,
    getLuckyWinnersFailure,
    getCurrentWinnersStart,
    getCurrentWinnersSuccess,
    getCurrentWinnersFailure,
    resetCasinoData,
    resetGameSearch,
    resetFilteredGames,
    updateGameSubCategorySuccess,
    updateRowsFetched,
    getCategoryProviderListStart,
    getCategoryProviderListSuccess,
    getCategoryProviderListFailure,
    getCategoryStudioListStart,
    getCategoryStudioListSuccess,
    getCategoryStudioListFailure,
    getRecentGamesStart,
    getRecentGamesComplete
  },
  reducer
} = createSlice({
  name: 'casinoMenus',
  initialState: INITIAL_STATE,
  reducers: {
    getGameCategoryStart: (state) => ({
      ...state,
      loading: true
    }),
    getGameCategorySuccess: (state, { payload }) => ({
      ...state,
      loading: false,
      gameCategory: payload
    }),
    getGameSearch: (state, { payload }) => ({
      ...state,
      filteredLoading: false,
      gameSearch: payload
    }),
    getGameCategoryFailure: (state) => ({
      ...state,
      loading: false
    }),
    getGameSubCategoryStart: (state) => ({
      ...state,
      subCatLoading: true
    }),
    getGameSubCategorySuccess: (state, { payload }) => {
      return ({
        ...state,
        subCatLoading: false,
        gameSubCategory: payload
      })
    },
    getGameSubCategoryFailure: (state) => ({
      ...state,
      subCatLoading: false
    }),
    resetCasinoData: (state) => ({
      ...state,
      games: {}
    }),
    getFavGameStart: (state) => ({
      ...state,
      favLoading: true
    }),
    getFavGameSuccess: (state, { payload }) => ({
      ...state,
      favLoading: false,
      favoriteGamesList: payload,
      fetched: true
    }),
    getFavGameFailure: (state) => ({
      ...state,
      favLoading: false
    }),
    getHomeDataStart: (state) => ({
      ...state,
      homeLoading: true
    }),
    getHomeDataSuccess: (state, { payload }) => ({
      ...state,
      homeLoading: false,
      gameCategory: payload.gameCategory
    }),
    getHomeDataFailure: (state) => ({
      ...state,
      homeLoading: false
    }),
    getProvidersStart: (state) => ({
      ...state,
      homeLoading: true
    }),
    getProvidersSuccess: (state, { payload }) => ({
      ...state,
      homeLoading: false,
      providers: payload
    }),
    getProvidersFailure: (state) => ({
      ...state,
      homeLoading: false
    }),
    getProvidersByOrderStart: (state) => ({
      ...state,
      homeLoading: true
    }),
    getProvidersByOrderSuccess: (state, { payload }) => ({
      ...state,
      homeLoading: false,
      providersByOrder: payload
    }),
    getProvidersByOrderFailure: (state) => ({
      ...state,
      homeLoading: false
    }),
    getStudiosStart: (state) => ({
      ...state,
      homeLoading: true
    }),
    getStudiosSuccess: (state, { payload }) => ({
      ...state,
      homeLoading: false,
      studios: payload
    }),
    getStudiosFailure: (state) => ({
      ...state,
      homeLoading: false
    }),
    setStudiosByOrder: (state, { payload }) => ({
      ...state,
      studiosByOrder: payload
    }),
    toggleFavoriteStart: (state) => ({
      ...state
    }),
    toggleFavoriteSuccess: (state, { payload }) => {
      if (payload.favoriteAction === 'REMOVE') {
        return {
          ...state,
          favoriteGamesList: {
            ...state.favoriteGamesList,
            count: state.favoriteGamesList.count - 1,
            rows: state.favoriteGamesList.rows.filter(favoriteGame => favoriteGame.id !== payload.id)
          }
        }
      }

      return { ...state }
    },
    toggleFavoriteFailure: (state) => ({
      ...state
    }),
    getFilteredGamesStart: (state, { payload }) => ({
      ...state,
      filteredLoading: !payload?.fetchMore,
      fetchMoreLoading: !!payload?.fetchMore
    }),
    getFilteredGamesSuccess: (state, { payload }) => ({
      ...state,
      filteredLoading: false,
      fetchMoreLoading: false,
      filteredGames: payload
    }),
    getFilteredGamesFailure: (state) => ({
      ...state,
      filteredLoading: false,
      fetchMoreLoading: false
    }),
    resetGameSearch: (state) => ({
      ...state,
      gameSearch: {}
    }),
    resetFilteredGames: (state) => ({
      ...state,
      filteredGames: INITIAL_STATE.filteredGames
    }),
    getTopGamesStart: (state) => ({
      ...state,
      loading: false
    }),
    getTopGamesSuccess: (state, { payload }) => ({
      ...state,
      loading: false,
      topGames: payload
    }),
    getTopGamesFailure: (state) => ({
      ...state,
      loading: false
    }),
    getTopWinnersStart: (state) => ({
      ...state,
      loading: false,
      topWinnersLoading: true
    }),
    getTopWinnersSuccess: (state, { payload }) => ({
      ...state,
      loading: false,
      topWinnersLoading: false,
      topWinners: payload
    }),
    getTopWinnersFailure: (state) => ({
      ...state,
      loading: false,
      topWinnersLoading: false
    }),
    getTopPlayersStart: (state) => ({
      ...state,
      topPlayersLoading: true
    }),
    getTopPlayersSuccess: (state, { payload }) => ({
      ...state,
      topPlayersLoading: false,
      topPlayers: payload
    }),
    getTopPlayersFailure: (state) => ({
      ...state,
      topPlayersLoading: false
    }),
    getHighWinnersStart: (state) => ({
      ...state,
      loading: false,
      highWinnersLoading: true
    }),
    getHighWinnersSuccess: (state, { payload }) => ({
      ...state,
      loading: false,
      highWinnersLoading: false,
      highWinners: payload
    }),
    getHighWinnersFailure: (state) => ({
      ...state,
      loading: false,
      highWinnersLoading: false
    }),
    getLuckyWinnersStart: (state) => ({
      ...state,
      loading: false,
      luckyWinnersLoading: true
    }),
    getLuckyWinnersSuccess: (state, { payload }) => ({
      ...state,
      loading: false,
      luckyWinnersLoading: false,
      luckyWinners: payload
    }),
    getLuckyWinnersFailure: (state) => ({
      ...state,
      loading: false,
      luckyWinnersLoading: false
    }),
    getCurrentWinnersStart: (state) => ({
      ...state,
      loading: false
    }),
    getCurrentWinnersSuccess: (state, { payload }) => ({
      ...state,
      loading: false,
      currentWinners: payload
    }),
    getCurrentWinnersFailure: (state) => ({
      ...state,
      loading: false
    }),
    updateGameSubCategorySuccess: (state, { payload }) => ({
      ...state,
      gameSubCategory: payload
    }),
    updateRowsFetched: (state, { payload }) => ({
      ...state,
      rowsFetchedCount: payload
    }),
    getCategoryProviderListStart: (state) => ({
      ...state,
      providerListLoading: true
    }),
    getCategoryProviderListSuccess: (state, { payload }) => ({
      ...state,
      providerListLoading: false,
      categoryProviderList: payload
    }),
    getCategoryProviderListFailure: (state, { payload }) => ({
      ...state,
      providerListLoading: false,
      categoryProviderList: payload
    }),
    getCategoryStudioListStart: (state) => ({
      ...state,
      studioListLoading: true
    }),
    getCategoryStudioListSuccess: (state, { payload }) => ({
      ...state,
      studioListLoading: false,
      categoryStudioList: payload
    }),
    getCategoryStudioListFailure: (state, { payload }) => ({
      ...state,
      studioListLoading: false,
      categoryStudioList: payload
    }),
    getRecentGamesStart: (state) => ({
      ...state,
      recentGamesLoading: true
    }),
    getRecentGamesComplete: (state, { payload }) => ({
      ...state,
      recentGames: payload,
      recentGamesLoading: false
    })
  }
})

export default reducer

export {
  getGameSubCategoryStart,
  getGameSubCategorySuccess,
  getGameSubCategoryFailure,
  getGameSearch,
  getFavGameStart,
  getFavGameSuccess,
  getFavGameFailure,
  getGameCategoryStart,
  getGameCategorySuccess,
  getGameCategoryFailure,
  getHomeDataStart,
  getHomeDataSuccess,
  getHomeDataFailure,
  getProvidersStart,
  getProvidersSuccess,
  getProvidersFailure,
  getProvidersByOrderStart,
  getProvidersByOrderSuccess,
  getProvidersByOrderFailure,
  getStudiosStart,
  getStudiosSuccess,
  getStudiosFailure,
  setStudiosByOrder,
  toggleFavoriteStart,
  toggleFavoriteSuccess,
  toggleFavoriteFailure,
  getFilteredGamesStart,
  getFilteredGamesSuccess,
  getFilteredGamesFailure,
  getTopGamesStart,
  getTopGamesSuccess,
  getTopGamesFailure,
  getTopWinnersStart,
  getTopWinnersSuccess,
  getTopWinnersFailure,
  getTopPlayersStart,
  getTopPlayersSuccess,
  getTopPlayersFailure,
  getHighWinnersStart,
  getHighWinnersSuccess,
  getHighWinnersFailure,
  getLuckyWinnersStart,
  getLuckyWinnersSuccess,
  getLuckyWinnersFailure,
  getCurrentWinnersStart,
  getCurrentWinnersSuccess,
  getCurrentWinnersFailure,
  resetCasinoData,
  resetGameSearch,
  resetFilteredGames,
  updateGameSubCategorySuccess,
  updateRowsFetched,
  getCategoryProviderListStart,
  getCategoryProviderListSuccess,
  getCategoryProviderListFailure,
  getCategoryStudioListStart,
  getCategoryStudioListSuccess,
  getCategoryStudioListFailure,
  getRecentGamesStart,
  getRecentGamesComplete
}
